import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Send, Loader2, Mail, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";
import {
  getTomorrowPlanning, getPlanningRecipients, sendTomorrowPlanning, recordPlanningArchive,
} from "@/lib/api";

const INCLUDE_OPTIONS = [
  { key: "weather", label: "EBCI Weather" },
  { key: "notams", label: "EBCI NOTAMs" },
  { key: "general_notes", label: "General Notes" },
];

export function SendPlanningButton() {
  const [open, setOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [planning, setPlanning] = useState(null);
  const [recipients, setRecipients] = useState("");
  const [saveDefault, setSaveDefault] = useState(false);
  const [include, setInclude] = useState({ weather: true, notams: true, general_notes: true });
  const [subject, setSubject] = useState("");
  const [extraMessage, setExtraMessage] = useState("");
  const [cargo, setCargo] = useState("");

  const buildOpts = (inc = include) => ({
    include: inc,
    subject: subject || undefined,
    extra_message: extraMessage || undefined,
    cargo: cargo || undefined,
  });

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    setExtraMessage("");
    setSubject("");
    setCargo("");
    const inc = { weather: true, notams: true, general_notes: true };
    setInclude(inc);
    try {
      const [p, r] = await Promise.all([
        getTomorrowPlanning({ include: inc }),
        getPlanningRecipients().catch(() => ({ data: { recipients: "" } })),
      ]);
      setPlanning(p.data);
      setSubject(p.data.subject || "");
      setRecipients(r.data.recipients || "");
      recordPlanningArchive({
        action: "generated",
        planning_date: p.data.date,
        flight_count: p.data.flight_count,
        subject: p.data.subject,
        html: p.data.html,
        text: p.data.text,
      }).catch(() => {});
    } catch (e) {
      toast.error("Could not build tomorrow's planning");
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshPreview = async (nextInclude, extra = extraMessage, subj = subject, crg = cargo) => {
    setRefreshing(true);
    try {
      const p = await getTomorrowPlanning({
        include: nextInclude,
        subject: subj || undefined,
        extra_message: extra || undefined,
        cargo: crg || undefined,
      });
      setPlanning(p.data);
    } catch (e) {
      toast.error("Could not refresh preview");
    } finally {
      setRefreshing(false);
    }
  };

  const toggleInclude = (key) => {
    const next = { ...include, [key]: !include[key] };
    setInclude(next);
    refreshPreview(next);
  };

  const doSend = async () => {
    if (!recipients.trim()) {
      toast.error("Please enter at least one recipient email");
      return;
    }
    setSending(true);
    try {
      const res = await sendTomorrowPlanning(recipients, saveDefault, buildOpts());
      if (res.data.sent) {
        toast.success(`Planning sent to ${res.data.recipients.length} recipient(s)`);
        // NOTE: the "sent" event is archived server-side (with the exact HTML
        // that was emailed, incl. the free note), so we don't record it here.
        setConfirmOpen(false);
        setOpen(false);
      } else {
        toast.error(res.data.error || "Could not send the planning");
      }
    } catch (e) {
      toast.error("Could not send the planning");
    } finally {
      setSending(false);
    }
  };

  const openInMailApp = async () => {
    if (!planning) return;
    // mailto cannot carry HTML, so copy the FORMATTED planning to the clipboard
    // (rich text/html). The user pastes it into the opened email body.
    let copied = false;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([planning.html], { type: "text/html" }),
            "text/plain": new Blob([planning.text], { type: "text/plain" }),
          }),
        ]);
        copied = true;
      }
    } catch (e) {
      copied = false;
    }
    const to = recipients.replace(/;/g, ",");
    recordPlanningArchive({
      action: "opened",
      planning_date: planning.date,
      recipients: to,
      flight_count: planning.flight_count,
      subject: planning.subject,
      html: planning.html,
      text: planning.text,
    }).catch(() => {});
    if (copied) {
      toast.success("Formatted planning copied — paste it (Ctrl/Cmd+V) into the email body");
      window.open(`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(planning.subject)}`, "_blank");
    } else {
      window.open(`mailto:${encodeURIComponent(to)}?subject=${encodeURIComponent(planning.subject)}&body=${encodeURIComponent(planning.text)}`, "_blank");
    }
  };

  const copyFormatted = async () => {
    if (!planning) return;
    try {
      if (navigator.clipboard && window.ClipboardItem) {
        await navigator.clipboard.write([
          new window.ClipboardItem({
            "text/html": new Blob([planning.html], { type: "text/html" }),
            "text/plain": new Blob([planning.text], { type: "text/plain" }),
          }),
        ]);
        toast.success("Formatted planning copied — paste it into any email");
      } else {
        await navigator.clipboard.writeText(planning.text);
        toast.success("Planning copied (plain text — browser blocks rich copy)");
      }
    } catch (e) {
      toast.error("Could not copy to clipboard");
      return;
    }
    recordPlanningArchive({
      action: "copied",
      planning_date: planning.date,
      recipients: recipients,
      flight_count: planning.flight_count,
      subject: planning.subject,
      html: planning.html,
      text: planning.text,
    }).catch(() => {});
  };

  return (
    <>
      <Button
        onClick={openDialog}
        size="sm"
        className="bg-[#0b2545] hover:bg-[#123a63] text-white gap-1.5"
        data-testid="send-planning-btn"
      >
        <Send size={15} /> Send Planning
      </Button>

      {/* Main dialog: preview + include options */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="send-planning-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0b2545] dark:text-white flex items-center gap-2">
              <Send size={18} /> Tomorrow's Flight Planning
            </DialogTitle>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#64748b]">
              <Loader2 className="animate-spin mr-2" size={20} /> Building planning…
            </div>
          ) : planning ? (
            <div className="flex flex-col gap-3 overflow-hidden">
              <div className="text-sm text-[#334155] dark:text-gray-300">
                <span className="font-semibold text-[#0b2545] dark:text-white">{planning.day_label}</span>
                {" · "}{planning.flight_count} flight(s)
              </div>

              {/* Include options */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-lg border border-[#e2e8f0] dark:border-slate-700 p-3">
                <span className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">Include:</span>
                <label className="flex items-center gap-2 text-sm text-[#94a3b8] cursor-not-allowed">
                  <Checkbox checked disabled data-testid="planning-include-flights" />
                  Flights (always)
                </label>
                {INCLUDE_OPTIONS.map((opt) => (
                  <label key={opt.key} className="flex items-center gap-2 text-sm text-[#334155] dark:text-gray-300 cursor-pointer" htmlFor={`inc-${opt.key}`}>
                    <Checkbox
                      id={`inc-${opt.key}`}
                      checked={include[opt.key]}
                      onCheckedChange={() => toggleInclude(opt.key)}
                      data-testid={`planning-include-${opt.key}`}
                    />
                    {opt.label}
                  </label>
                ))}
              </div>

              {/* Planning Cargo — free text, appears under Flights in the email.
                  Copy-paste supported; line breaks are preserved. */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                  📦 Planning Cargo (optional)
                </label>
                <Textarea
                  value={cargo}
                  onChange={(e) => setCargo(e.target.value)}
                  onBlur={() => refreshPreview(include)}
                  placeholder={"Paste any cargo foreseen for tomorrow here.\nLine breaks are kept exactly as entered."}
                  rows={4}
                  className="font-mono text-sm whitespace-pre-wrap"
                  data-testid="planning-cargo-input"
                />
                <span className="text-xs text-[#94a3b8]">Shown as a "Planning Cargo" section just under Flights.</span>
              </div>

              {/* Preview */}
              <div
                className="relative border border-[#e2e8f0] dark:border-slate-700 rounded-lg overflow-auto bg-white"
                style={{ maxHeight: "42vh" }}
                data-testid="planning-preview"
              >
                {refreshing && (
                  <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                    <Loader2 className="animate-spin text-[#64748b]" size={20} />
                  </div>
                )}
                <div className="p-2" dangerouslySetInnerHTML={{ __html: planning.html }} />
              </div>

              {/* Recipients */}
              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                  Recipients (comma-separated)
                </label>
                <Input
                  value={recipients}
                  onChange={(e) => setRecipients(e.target.value)}
                  placeholder="ops@charleroi-airport.com, duty.manager@…"
                  data-testid="planning-recipients-input"
                />
              </div>
            </div>
          ) : null}

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={copyFormatted}
              disabled={loading || !planning}
              className="gap-1.5"
              data-testid="planning-copy-formatted"
            >
              <Copy size={15} /> Copy formatted
            </Button>
            <Button
              variant="outline"
              onClick={openInMailApp}
              disabled={loading || !planning}
              className="gap-1.5"
              data-testid="planning-open-mail-app"
            >
              <ExternalLink size={15} /> Open in my mail app
            </Button>
            <Button
              onClick={() => { setSubject(planning?.subject || subject); setConfirmOpen(true); }}
              disabled={loading || !planning}
              className="bg-[#0b2545] hover:bg-[#123a63] text-white gap-1.5"
              data-testid="planning-send-bsca"
            >
              <Mail size={15} /> Send from BSCA
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm dialog: review / edit before sending */}
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-lg" data-testid="planning-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0b2545] dark:text-white flex items-center gap-2">
              <Mail size={18} /> Review &amp; Send
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">Recipients</label>
              <Input
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                placeholder="ops@charleroi-airport.com, duty.manager@…"
                data-testid="planning-confirm-recipients"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">Subject</label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="planning-confirm-subject"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xs font-semibold uppercase tracking-wide text-[#64748b]">
                Additional message (optional)
              </label>
              <Textarea
                value={extraMessage}
                onChange={(e) => setExtraMessage(e.target.value)}
                placeholder="e.g. VIP arrival at 09:00, expect press. Fuel bowser out of service until noon."
                rows={4}
                data-testid="planning-confirm-message"
              />
              <span className="text-xs text-[#94a3b8]">Shown as a highlighted note at the top of the email.</span>
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-[#334155] dark:text-gray-300" htmlFor="save-default-recipients">
              <Checkbox
                id="save-default-recipients"
                checked={saveDefault}
                onCheckedChange={setSaveDefault}
                data-testid="planning-save-default"
              />
              Save these as my default recipients
            </label>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)} data-testid="planning-confirm-cancel">
              Cancel
            </Button>
            <Button
              onClick={doSend}
              disabled={sending}
              className="bg-[#0b2545] hover:bg-[#123a63] text-white gap-1.5"
              data-testid="planning-confirm-send"
            >
              {sending ? <Loader2 className="animate-spin" size={15} /> : <Send size={15} />}
              Confirm &amp; Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
